import { format, isWeekend } from "date-fns"
import { DateRange } from "@mui/x-date-pickers-pro"
import { useCallback, useEffect, useState } from "react"
import { setSelectedDays } from "../../../../redux/reducers/timeOffRequestsReducer"
import { AbsenceDayRequest } from "../../../../types/absence"
import { DateFormats } from "../../../../api/absence/tempTypes/generalprops"
import { useAppDispatch } from "../../../../redux/store"
import { WfmAbsence } from "../../../../services/actionsService"
import { nextWorkingDay } from "../../../../utils/date-utils"
import { DateRangeValidationError } from "../../../../shared/UI/DateRangePickerV2"

const useAbsenceDateRange = (
  departmentWorksWeekends: boolean,
  wfmAbsence?: WfmAbsence
) => {
  const dispatch = useAppDispatch()

  const [absenceDates, setAbsenceDates] = useState<DateRange<Date>>([
    wfmAbsence ? new Date(wfmAbsence.scheduleStartDate) : new Date(),
    wfmAbsence ? new Date(wfmAbsence.scheduleStartDate) : new Date()
  ])
  const [dateRangeError, setDateRangeError] = useState<DateRangeValidationError>(null)
  const [disableEditing, setDisableEditing] = useState<boolean>(false)
  const [expectedReturn, setExpectedReturn] = useState<Date | null>(new Date())
  const [expectedReturnError, setExpectedReturnError] = useState<string | null>(null)

  const setRangeDatesFromStrings = useCallback((
    startDate: string,
    endDate: string,
  ) => {
    const days: AbsenceDayRequest[] = []
    const currentDate = new Date(startDate)
    const tillDate = new Date(endDate)

    currentDate.setHours(0)
    currentDate.setMinutes(0)
    currentDate.setSeconds(0)

    tillDate.setHours(23)
    tillDate.setMinutes(59)
    tillDate.setSeconds(59)

    while (currentDate <= tillDate) {
      const date = new Date(format(currentDate, DateFormats.DATE_AND_TIME))
      currentDate.setDate(currentDate.getDate() + 1)
      days.push({
        day: date,
        hours: wfmAbsence?.absenceLength ?? 8,
        checked: departmentWorksWeekends ? true : !isWeekend(date),
      })
    }

    dispatch(setSelectedDays(days))
  }, [departmentWorksWeekends, dispatch, wfmAbsence?.absenceLength])

  useEffect(() => {
    setDisableEditing(Boolean(wfmAbsence))

    if (!wfmAbsence) {
      return
    }

    /* use the schedule start date but keep the absence time */
    const combinedDateTime = new Date(wfmAbsence.scheduleStartDate)
    const absenceTime = new Date(wfmAbsence.absenceStartDate)

    combinedDateTime.setHours(
      absenceTime.getHours(),
      absenceTime.getMinutes(), 
      absenceTime.getSeconds(),
      absenceTime.getMilliseconds()
    )

    setRangeDatesFromStrings(format(combinedDateTime, DateFormats.DATE_AND_TIME), format(combinedDateTime, DateFormats.DATE_AND_TIME))
    setAbsenceDates([ new Date(combinedDateTime), new Date(combinedDateTime) ]) 
  }, [setRangeDatesFromStrings, wfmAbsence])

  useEffect(() => {
    if (!absenceDates[0] || !absenceDates[1]) {
      dispatch(setSelectedDays(null))
      return
    }

    setRangeDatesFromStrings(
      format(absenceDates[0], DateFormats.Date),
      format(absenceDates[1], DateFormats.Date))
  }, [absenceDates, absenceDates[0], absenceDates[1], departmentWorksWeekends, dispatch, setRangeDatesFromStrings])

  useEffect(() => {
    const lastDayOfAbsence = absenceDates[1]
    if (!lastDayOfAbsence) {
      return
    }

    const newExpectedReturn = nextWorkingDay(lastDayOfAbsence, departmentWorksWeekends)
    setExpectedReturn(newExpectedReturn)
  }, [absenceDates, departmentWorksWeekends])

  return {
    absenceDates,
    setAbsenceDates,
    disableEditing,
    expectedReturn,
    setExpectedReturn,
    dateRangeError,
    setDateRangeError,
    expectedReturnError,
    setExpectedReturnError
  }
}

export default useAbsenceDateRange